import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  IconButton,
  Typography,
  Tooltip,
} from '@mui/material';
import {
  Visibility,
  PostAdd,
  CheckCircle,
  Check,
  RadioButtonUnchecked,
  RadioButtonChecked,
  Circle,
  ChangeHistory,
  Close,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "../accordion";

// 데이터 가공 로직 분리
const prepareMaterialTableData = (gproject) => {
  const headers = [
    {
      type: 'HEADER',
      label: '구분',
      rowspan: 2,
      colspan: 2,
      hiddenSubHeaders: [
        { width: '10%' },
        { width: '12%' }
      ]
    },
    {
      type: 'HEADER',
      label: '제품명',
      rowspan: 2,
      hiddenSubHeaders: [
        { width: '' },
        { width: '15%' }
      ]
    },
    {
      type: 'HEADER',
      label: '제조사',
      colspan: 5,
      subHeaders: [
        { label: '자재', width: '7%' },
        { label: '배강도', width: '7%' },
        { label: '강화', width: '7%' },
        { label: '접합', width: '7%' },
        { label: '복층', width: '7%' }
      ]
    }
  ];

  const rows = (() => {
    // 유리 데이터
    const glassRows = gproject.selectedGlasses.map(glass => {
      const items = [];
      let totalThickness = 0;

      glass.selectedGcomponentItems.forEach((layer) => {
        let glassProductName = null;
        let manufacturer = null;
        let thickness = null;
        let heatTreating = null;

        let idLaminated = null;
        let idInsulatedGlazing = null;
        
        layer.forEach((item) => {
          if (item.code === 'GLASS PRODUCT NAME') {
            glassProductName = item.value.name;
            manufacturer = item.value.gclients[0].name;
          } else if (item.code === 'GL THICKNESS') {
            thickness = item.value.code;
            totalThickness += parseFloat(thickness) || 0;
          } else if (item.code === 'GL HEAT TREATING') {
            heatTreating = item.value.code;
          } else if (item.code === 'FILM THICKNESS') {
            idLaminated = glass.id;
            totalThickness += parseFloat(item.value.code) || 0;
          } else if (item.code === 'SPACER THICKNESS') {
            idInsulatedGlazing = glass.id;
            totalThickness += parseFloat(item.value.code) || 0;
          }
        });
        
        let heatStrengthened = null;
        let tempered = null;

        if (heatTreating) {
          const getProcessInfo = (type) => {
            const process = glass.selectedProcessGClients.find(p => p.type === type);
            if (!process) return '';
            const thickness = process.specificationElements?.find(ele => ele.code === 'GL THICKNESS')?.value.code || '';
            return `${thickness}T\n${process.selectedGClients.map(client => client.name).join(", ")}`;
          };

          if (heatTreating === 'H/S') {
            heatStrengthened = getProcessInfo("HEAT_STRENGTHENED");
          } else if (heatTreating === 'F/T') {
            tempered = getProcessInfo("TEMPERED");
          }
        }
        
        if (glassProductName) {
          const product2 = `${thickness} ${glassProductName}\n${manufacturer}`;
          const existingItem = items.find(item => item.product2 === product2);
          
          if (existingItem) {
            if (heatStrengthened) existingItem.processing.HEAT_STRENGTHENED = heatStrengthened;
            if (tempered) existingItem.processing.TEMPERED = tempered;
          } else {
            items.push({
              id: `${glass.id}`,
              no: glass.no,
              product1: glass.specification || '',
              product2: product2,
              processing: {
                HEAT_STRENGTHENED: heatStrengthened,
                TEMPERED: tempered,
                LAMINATED: '',
                INSULATED_GLASS_UNIT: '',
              }
            });
          }
        } else {
          const getProcessInfo = (type) => {
            const process = glass.selectedProcessGClients.find(p => p.type === type);
            if (!process) return '';

            if (type === 'LAMINATED') {
              let filmType = '';
              let totalThickness = 0;
              
              process.specificationElements?.forEach(ele => {
                if (ele.code === 'FILM THICKNESS' || ele.code === 'GL THICKNESS' || ele.code === 'GL THICKNESS2') {
                  totalThickness += parseFloat(ele.value.code) || 0;
                } else if (ele.code === 'FILM TYPE') {
                  filmType = ele.value.name.replace(/\(.*?\)/, "");
                }
              });

              return `${totalThickness} ${filmType}\n${process.selectedGClients.map(client => client.name).join(", ")}`;
            } else if (type === 'INSULATED_GLASS_UNIT') {
              let totalThickness = 0;
              let gasMaterial = '';
              
              process.specificationElements?.forEach(ele => {
                if (ele.code === 'GL THICKNESS' || ele.code === 'GL THICKNESS2' || ele.code === 'SPACER THICKNESS') {
                  totalThickness += parseFloat(ele.value.code) || 0;
                } else if (ele.code === 'GAS MATERIAL') {
                  gasMaterial = ele.value.code;
                }
              });

              const gasText = gasMaterial === 'AR' ? ' 가스주입단열유리' : '';
              return `${totalThickness}T${gasText}\n${process.selectedGClients.map(client => client.name).join(", ")}`;
            }
          };

          if (idLaminated) {
            items.forEach(item => {
              if (item.id === idLaminated) {
                item.processing.LAMINATED = getProcessInfo("LAMINATED");
              }
            });
          }

          if (idInsulatedGlazing) {
            items.forEach(item => {
              if (item.id === idInsulatedGlazing) {
                item.processing.INSULATED_GLASS_UNIT = getProcessInfo("INSULATED_GLASS_UNIT");
              }
            });
          }
        }
      });

      return {
        type: 'RAW_MATERIAL',
        id: glass.id,
        division1: `${totalThickness}T ${glass.gtypeName}`,
        items: items.length > 0 ? items : [{
          id: glass.id,
          no: glass.no,
          product1: glass.specification || '',
          product2: '',
          processing: {
            HEAT_STRENGTHENED: '',
            TEMPERED: '',
            LAMINATED: '',
            INSULATED_GLASS_UNIT: '',
          }
        }]
      };
    });
    
    // 가공부자재 데이터 그룹화
    const processItems = gproject.selectedSubMaterialProcessItems
      .filter(item => item.name && item.value && Object.keys(item.value).length > 0);
    
    const processGroup = processItems.length > 0 ? [{
      type: 'SUB_MATERIAL_PROCESS',
      id: 'process-group',
      division1: '가공부자재',
      items: processItems.map(item => ({
        id: item.id,
        no: item.name,
        product1: item.value?.name || '',
        product2: item.value?.gclients?.[0]?.name || '',
        processing: {
          HEAT_STRENGTHENED: '',
          TEMPERED: '',
          LAMINATED: '',
          INSULATED_GLASS_UNIT: ''
        }
      }))
    }] : [];
    
    // 시공부자재 데이터 그룹화
    const buildItems = gproject.selectedSubMaterialBuildItems
      .filter(item => item.name && item.value && Object.keys(item.value).length > 0);
    
    const buildGroup = buildItems.length > 0 ? [{
      type: 'SUB_MATERIAL_BUILD',
      id: 'build-group',
      division1: '시공부자재',
      items: buildItems.map(item => ({
        id: item.id,
        no: item.name,
        product1: item.value?.name || '',
        product2: item.value?.gclients?.[0]?.name || '',
        processing: {
          HEAT_STRENGTHENED: '',
          TEMPERED: '',
          LAMINATED: '',
          INSULATED_GLASS_UNIT: ''
        }
      }))
    }] : [];
    
    console.log(glassRows, processGroup, buildGroup);
    return [...glassRows, ...processGroup, ...buildGroup];
  })();

  return { headers, rows };
};

const prepareDocumentTableData = (itemsWithDocus, gproject, selected) => {
  const headers = [
    {
      type: 'HEADER',
      label: '구분',
      rowspan: 2,
      width: '7%',
    },
    {
      type: 'HEADER', 
      label: '업체명',
      rowspan: 2,
      width: '10%',
    },
    {
      type: 'HEADER',
      label: '일반서류',
      colspan: 7,
      subHeaders: [
        { label: '사업자\n등록증', width: '4%' },
        { label: '공장\n등록증', width: '4%' },
        { label: '국세\n납세증명서', width: '4%' },
        { label: '지방세\n납세증명서', width: '4%' },
        { label: '건설업\n등록증', width: '4%' },
        { label: '회사\n소개자료', width: '4%' },
        { label: '기타', width: '8%' },
      ],
    },
    {
      type: 'HEADER',
      label: '기술문서',
      colspan: 6,
      subHeaders: [
        { label: '제품명', width: '15%' },
        { label: '인증서', width: '11%', colspan: 2 },
        { label: '성적서', width: '4%' },
        { label: '시험성과\n대비표', width: '4%' },
        { label: '기타', width: '7%' },
      ]
    }
  ];

  const transformRowDivisionData = [
    {
      id: 'SUPPLIER',
      label: '시공업체'
    },
    {
      id: 'RAW_MATERIAL',
      label: '원판'
    },
    {
      id: 'PROCESS',
      label: '가공'
    },
    {
      id: 'SUB_MATERIAL_PROCESS',
      label: '가공부자재'
    },
    {
      id: 'SUB_MATERIAL_BUILD',
      label: '시공부자재'
    }
  ];
  
  const generalDocus = [
    { label: '사업자등록증', required: true },
    { label: '공장등록증', required: true },
    { label: '국세 납세증명서', required: true },
    { label: '지방세 납세증명서', required: true },
    { label: '건설업등록증', required: gproject.contractor === 'Y' }, // 건설업등록증은 원도급/하도급사만 제출해야 하는 필수서류임
    { label: '회사소개자료', required: false },
    { label: 'ETC', required: false },
  ];
  
  const rows = itemsWithDocus
    .filter(item => transformRowDivisionData.find(data => data.id === item.id))
    .flatMap(item => {
      const rowData = transformRowDivisionData.find(data => data.id === item.id);
      const companies = item.children?.map(child => {
        const generalDoc = child.children?.find(doc => doc.type === 'GENERALS'); // 일반서류
        
        const documentCells = generalDocus.map(docType => {
          const doc = generalDoc?.children?.find(doc => doc.label === docType.label);
          const isConstructionReg = docType.label === '건설업등록증';
          const isSupplier = rowData?.id === 'SUPPLIER';
          const isRequired = isConstructionReg ? 
            (isSupplier && gproject.contractor === 'Y') : 
            docType.required;

          return {
            label: docType.label,
            hasPath: !!doc?.data?.documentPath,
            valid: doc?.data?.valid,
            validYN: doc?.data?.validYN,
            required: isRequired
          };
        });

        // 일반문서의 기타 문서 찾기 (generalDocus에 해당되지 않는 문서들)
        const otherDocs = generalDoc?.children?.filter(doc => 
          !generalDocus.some(d => d.label === doc.label)
        ).map(doc => ({
          label: doc.label,
          hasPath: !!doc.data?.documentPath,
          valid: doc.data?.valid,
          validYN: doc.data?.validYN
        })) || [];

        // 기타 문서가 있으면 마지막 컬럼에 문서 이름들을 나열
        if (otherDocs.length > 0) {
          documentCells[documentCells.length - 1] = otherDocs;
        }

        console.log('documentCells', documentCells);

        const certDoc = child.children?.find(doc => doc.type === 'CERTIFICATIONS'); // 인증서
        
        const testDoc = child.children?.find(doc => doc.type === 'TESTS'); // 성적서

        const etcDoc = child.children?.find(doc => doc.type === 'ETCS'); // 기타
        
        // 기술문서의 기타 문서 처리
        const etcDocs = [
          // CERTIFICATIONS 타입에서 g04GeneralDocuId가 있는 문서
          ...(certDoc?.children?.filter(doc => doc.data?.g04GeneralDocuId)?.map(doc => ({
            label: doc.label,
            hasPath: !!doc.data?.documentPath
          })) || []),
          // TESTS 타입에서 g04GeneralDocuId가 있는 문서
          ...(testDoc?.children?.flatMap(child => 
            child.children?.filter(doc => doc.data?.g04GeneralDocuId)?.map(doc => ({
              label: doc.label,
              hasPath: !!doc.data?.documentPath
            })) || []
          ) || []),
          // ETCS 타입의 기타 문서
          ...(etcDoc?.children?.map(doc => ({
            label: doc.label,
            hasPath: !!doc.data?.documentPath
          })) || []),
        ];

        // 제품명 목록 가져오기
        // RAW_MATERIAL: TESTS > children > children의 code와 label 조합
        // PROCESS: TESTS > children > children의 label
        // SUB_MATERIAL_PROCESS/BUILD: TESTS > children > children > children의 label과 상위 label 조합
        let productNames = [];
        if (rowData?.id === 'RAW_MATERIAL') {
          productNames = testDoc?.children?.flatMap(child => 
            (child.children || []).filter(gchild => gchild.data.documentType === 'TEST').map(doc => {
              // 인증서 노드에서 DOCCERTIFICATION 타입 노드 찾기
              const certificationDoc = certDoc.children?.find(
                certDoc => certDoc.type === 'DOCCERTIFICATION' && 
                certDoc.data?.g04docuGCertificationId === doc.data?.g04docuGCertificationId
              );
              
              return {
                name: `${doc.data.code} ${child.label}`,
                hasDoc: !!doc.data?.documentPath,
                hasPerformanceDoc: !doc.data?.documentPath && !!doc.data?.performanceData?.performanceDocumentPath,
                hasComparisonDoc: !!doc.data?.gtestComparisonDocumentPath,
                g04docuGCertificationName: doc.data?.gstandardNo ? `[ ${doc.data?.gstandardNo} ]\n${doc.data?.g04docuGCertificationName}` : doc.data?.g04docuGCertificationName || '',
                hasCertificationDoc: !!certificationDoc?.data?.documentPath,
                isValid: doc.data?.valid,
                isValidYN: doc.data?.validYN,
                certificationId: doc.data?.g04docuGCertificationId
              };
            })
          ) || [];
        } else if (rowData?.id === 'PROCESS') {
          productNames = testDoc?.children?.flatMap(child => 
            (child.children || []).map(doc => {
              // 인증서 노드에서 DOCCERTIFICATION 타입 노드 찾기
              const certificationDoc = certDoc.children?.find(
                certDoc => certDoc.type === 'DOCCERTIFICATION' && 
                certDoc.data?.g04docuGCertificationId === doc.data?.g04docuGCertificationId
              );
              
              return {
                name: doc.label,
                hasDoc: !!doc.data?.documentPath,
                hasPerformanceDoc: !doc.data?.documentPath && !!doc.data?.performanceData?.performanceDocumentPath,
                hasComparisonDoc: !!doc.data?.gtestComparisonDocumentPath,
                g04docuGCertificationName: doc?.data?.gstandardNo ? `[ ${doc?.data?.gstandardNo} ]\n${doc.data?.g04docuGCertificationName}` : doc.data?.g04docuGCertificationName || '',
                hasCertificationDoc: !!certificationDoc?.data?.documentPath,
                isValid: doc.data?.valid,
                isValidYN: doc.data?.validYN,
                certificationId: doc.data?.g04docuGCertificationId
              };
            })
          ) || [];
        } else if (rowData?.id === 'SUB_MATERIAL_PROCESS' || rowData?.id === 'SUB_MATERIAL_BUILD') {
          productNames = testDoc?.children?.flatMap(child => 
            child.children?.flatMap(subChild => 
              (subChild?.children || []).map(doc => {
                // 인증서 노드에서 DOCCERTIFICATION 타입 노드 찾기
                const certificationDoc = certDoc?.children?.find(
                  certDoc => certDoc.type === 'DOCCERTIFICATION' && 
                  (
                    // 시가공부자재의 경우 같은 표준이어도 인증규격(G04docuGCertifications)이 다를 수 있으므로 인증서가 여러개 포함될 수 있다.
                    // 그래서 중복된 인증서를 제거하는 로직이 목차 트리 구성할 때 있는데 이 때 삭제된 g04docuGCertificationId를 배열에 담아두어 아래처럼 찾을 때 사용했다.
                    certDoc.data?.g04docuGCertificationId === doc.data?.g04docuGCertificationId || 
                    (
                      certDoc.data?.g04docuGCertificationIds &&
                      Array.isArray(certDoc.data?.g04docuGCertificationIds) &&
                      certDoc.data?.g04docuGCertificationIds.length > 0 &&
                      certDoc.data?.g04docuGCertificationIds.includes(subChild.data?.g04docuGCertificationId)
                    )
                  )
                );

                return {
                  // 시가공부자재는 하위 스펙이 없는 경우 성적서 이름을 등록하는데 이때 성적서 이름을 입력하는 기준이 없다보니
                  // 보통 제품과 동일한 이름을 주게 되고 그럴 경우 같은 이름이 반복되므로 아래와 같이 처리
                  name: subChild?.label === doc.label ? subChild.label : `${subChild?.label} ${doc.label}`,
                  hasDoc: !!doc.data?.documentPath,
                  hasComparisonDoc: !!doc.data?.gtestComparisonDocumentPath,
                  g04docuGCertificationName: doc?.data?.gstandardNo ? `[ ${doc?.data?.gstandardNo} ]\n${doc.data?.g04docuGCertificationName}` : doc.data?.g04docuGCertificationName || '',
                  hasCertificationDoc: !!certificationDoc?.data?.documentPath,
                  isValid: doc.data?.valid,
                  isValidYN: doc.data?.validYN,
                  certificationId: doc.data?.g04docuGCertificationId
                };
              })
            ) || []
          ) || [];
        }

        return {
          label: child.label,
          documentCells,
          productNames,
          etcDocs
        };
      }) || [];
      // 회사가 없는 경우 하나의 행만 반환
      if (companies.length === 0) {
        return [{
          id: item.id,
          label: rowData?.label,
          company: '',
          documentCells: Array(generalDocus.length).fill(''),
          productNames: [],
          labelRowspan: 1,
          companyRowspan: 1,
          productName: ''
        }];
      }
      // 회사별로만 구분/회사명 머지
      return companies.flatMap((company, companyIndex) => {
        const productCount = company.productNames.length || 1;
        // 제품이 없는 경우 하나의 행만 반환
        if (productCount === 1 && company.productNames.length === 0) {
          return [{
            id: `${item.id}-${companyIndex}`,
            label: rowData?.label,
            company: company.label.split(':')[0],
            documentCells: company.documentCells,
            productName: '',
            labelRowspan: 1,
            companyRowspan: 1,
            etcDocs: company.etcDocs
          }];
        }
        // 각 제품마다 별도의 행 생성, 첫 번째 행에만 rowspan
        return company.productNames.map((productName, productIndex) => ({
          id: `${item.id}-${companyIndex}-${productIndex}`,
          label: rowData?.label,
          company: company.label.split(':')[0],
          documentCells: company.documentCells,
          productName,
          labelRowspan: productIndex === 0 ? productCount : 0,
          companyRowspan: productIndex === 0 ? productCount : 0,
          etcDocs: company.etcDocs
        }));
      });
    });

  console.log('rows', rows);
  console.log('selected', selected);
  return { headers, rows };
};

const GProjectG04ContentTableView = ({
  from,
  crudMode,
  gproject,
  itemsWithDocus,
  filteredItems,
  selected,
  onSelect,
  disableMultiParentSelection,
  handleClickViewEachDoc,
  handleClickViewSummary,
  handleClickAddSubstituteTestDoc,
  reGenerate,
  // onTableHtmlGenerated,
  materialTableData,
  documentTableData,
}) => {
  // console.log('filteredItems', filteredItems);
  // console.log('itemsWithDocus', itemsWithDocus);
  // console.log('gproject', gproject);
  // 데이터를 평탄화하여 테이블에 표시할 수 있는 형태로 변환
  const flattenData = (items) => {
    const flattened = [];
    
    const processNode = (node, level = 0, parentPath = '') => {
      const currentPath = parentPath ? `${parentPath}.${node.id}` : node.id;
      
      // 현재 노드 정보 추가
      flattened.push({
        id: node.id,
        label: node.label,
        type: node.type,
        level,
        path: currentPath,
        data: node.data,
        hasDocument: node.data?.documentPath ? true : false,
        isValid: node.data?.valid,
        isValidYN: node.data?.validYN,
        hasPerformanceDoc: node.data?.performanceData?.performanceDocumentPath ? true : false,
        isSubstitute: node.data?.substitute === 'Y',
      });

      // 자식 노드 처리
      if (node.children && Array.isArray(node.children)) {
        node.children.forEach(child => processNode(child, level + 1, currentPath));
      }
    };

    items.forEach(item => processNode(item));
    return flattened;
  };

  const flattenedData = flattenData(filteredItems);

  // const materialTableData = prepareMaterialTableData(gproject);

  // const documentTableData = prepareDocumentTableData(itemsWithDocus, gproject, selected);

  
  // console.log('materialTableData:', materialTableData);

  const handleSelect = (id) => {
    onSelect((prevSelected) => {
      if (prevSelected.includes(id)) {
        return prevSelected.filter((prevSelect) => prevSelect !== id);
      }
      return [...prevSelected, id];
    });
  };

  const getIndentation = (level) => {
    return level * 2; // 2rem per level
  };

  const getStatusIcon = (row) => {
    if (row.hasDocument) {
      return row.isValid ? (
        <CheckCircle color="success" fontSize="small" />
      ) : (
        <CheckCircle color="error" fontSize="small" />
      );
    }
    return null;
  };

  const getRowBackgroundColor = (type) => {
    switch (type) {
      case 'RAW_MATERIAL':
        return 'rgba(187, 222, 251, 0.3)'; // 유리 - 부드러운 파스텔 블루
      case 'SUB_MATERIAL_PROCESS':
        return 'rgba(255, 236, 179, 0.3)'; // 가공부자재 - 부드러운 파스텔 옐로우
      case 'SUB_MATERIAL_BUILD':
        return 'rgba(220, 237, 200, 0.3)'; // 시공부자재 - 부드러운 파스텔 그린
      default:
        return 'inherit';
    }
  };

  const getBackgroundColor = (type) => {
    switch (type) {
      case 'HEADER':
        return 'rgba(0, 0, 0, 0.45)';
      default:
        return 'inherit';
    }
  };

  const getIconColor = () => {
    return 'rgba(0, 0, 0, 0.45)';
  };

  const [legendHover, setLegendHover] = useState(null);
  const [selectedLegends, setSelectedLegends] = useState([]);
  const [materialExpanded, setMaterialExpanded] = useState(true);
  const [documentExpanded, setDocumentExpanded] = useState(true);

  // 디버깅을 위한 useEffect 추가
  useEffect(() => {
    console.log('legendHover changed:', legendHover);
  }, [legendHover]);

  useEffect(() => {
    console.log('selectedLegends changed:', selectedLegends);
  }, [selectedLegends]);

  // 범례 토글 핸들러
  const handleLegendToggle = (type) => {
    console.log('Legend toggle:', type);
    setSelectedLegends(prev => {
      if (prev.includes(type)) {
        return prev.filter(legend => legend !== type);
      } else {
        return [...prev, type];
      }
    });
  };

  // 범례 이벤트 핸들러 수정
  const handleLegendHover = (type) => {
    console.log('Legend hover:', type);
    setLegendHover(type);
  };

  const handleLegendLeave = () => {
    console.log('Legend leave');
    setLegendHover(null);
  };

  // 범례 클릭 이벤트 핸들러 추가
  const handleLegendClick = (event, type) => {
    event.stopPropagation(); // 이벤트 버블링 방지
    handleLegendToggle(type);
  };

  // 테이블 HTML 생성 함수
  // const generateTableHtml = () => {
  //   const materialTableHtml = `
  //     <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px;">
  //       <thead>
  //         <tr>
  //           ${materialTableData.headers.map(header => `
  //             <th colspan="${header.colspan || 1}" rowspan="${header.rowspan || 1}" 
  //                 style="background-color: rgba(0, 0, 0, 0.45); color: white; font-weight: bold; text-align: center; border: 1.5px solid rgba(0, 0, 0, 0.12);">
  //               ${header.label}
  //             </th>
  //           `).join('')}
  //         </tr>
  //         <tr>
  //           ${materialTableData.headers.map(header => 
  //             header.subHeaders?.map(subHeader => `
  //               <th style="background-color: rgba(0, 0, 0, 0.45); color: white; font-weight: bold; text-align: center; border: 1.5px solid rgba(0, 0, 0, 0.12); width: ${subHeader.width};">
  //                 ${subHeader.label}
  //               </th>
  //             `).join('') || ''
  //           ).join('')}
  //         </tr>
  //       </thead>
  //       <tbody>
  //         ${materialTableData.rows.map(row => {
  //           if (row.items) {
  //             if (row.type === 'RAW_MATERIAL') {
  //               const groupedItems = row.items.reduce((acc, item) => {
  //                 const key = `${item.no}-${item.product1}`;
  //                 if (!acc[key]) {
  //                   acc[key] = {
  //                     items: [item],
  //                     no: item.no,
  //                     product1: item.product1,
  //                     processing: { ...item.processing }
  //                   };
  //                 } else {
  //                   acc[key].items.push(item);
  //                   if (item.processing.LAMINATED) {
  //                     acc[key].processing.LAMINATED = item.processing.LAMINATED;
  //                   }
  //                   if (item.processing.INSULATED_GLASS_UNIT) {
  //                     acc[key].processing.INSULATED_GLASS_UNIT = item.processing.INSULATED_GLASS_UNIT;
  //                   }
  //                 }
  //                 return acc;
  //               }, {});

  //               return Object.values(groupedItems).map((group, groupIndex) => 
  //                 group.items.map((item, itemIndex) => `
  //                   <tr style="background-color: ${getRowBackgroundColor(row.type)};">
  //                     ${groupIndex === 0 && itemIndex === 0 ? `
  //                       <td rowspan="${row.items.length}" colspan="2" style="border: 1.5px solid rgba(0, 0, 0, 0.12);">${row.division1}</td>
  //                     ` : ''}
  //                     ${itemIndex === 0 ? `
  //                       <td rowspan="${group.items.length}" style="border: 1.5px solid rgba(0, 0, 0, 0.12);">${group.product1.replaceAll("|", " ").replaceAll("+", " + ")}</td>
  //                     ` : ''}
  //                     <td style="border: 1.5px solid rgba(0, 0, 0, 0.12); white-space: pre-line; text-align: center;">
  //                       ${item.product2.split('\n').map((line, index) => 
  //                         index === 1 ? 
  //                           `<div style="color: #1976d2; padding: 4px 8px; border-radius: 4px; display: inline-block; margin-top: 4px; font-weight: normal;">${line}</div>` :
  //                           `${line.replace(/\(.*?\)/, "")}${index === 0 ? '<br>' : ''}`
  //                       ).join('')}
  //                     </td>
  //                     <td style="border: 1.5px solid rgba(0, 0, 0, 0.12); white-space: pre-line; text-align: center;">
  //                       ${item.processing.HEAT_STRENGTHENED ? item.processing.HEAT_STRENGTHENED.split('\n').map((line, index) => 
  //                         index === 1 ? 
  //                           `<div style="color: #1976d2; padding: 4px 8px; border-radius: 4px; display: inline-block; margin-top: 4px; font-weight: normal;">${line}</div>` :
  //                           `${line}${index === 0 ? '<br>' : ''}`
  //                       ).join('') : ''}
  //                     </td>
  //                     <td style="border: 1.5px solid rgba(0, 0, 0, 0.12); white-space: pre-line; text-align: center;">
  //                       ${item.processing.TEMPERED ? item.processing.TEMPERED.split('\n').map((line, index) => 
  //                         index === 1 ? 
  //                           `<div style="color: #1976d2; padding: 4px 8px; border-radius: 4px; display: inline-block; margin-top: 4px; font-weight: normal;">${line}</div>` :
  //                           `${line}${index === 0 ? '<br>' : ''}`
  //                       ).join('') : ''}
  //                     </td>
  //                     ${itemIndex === 0 ? `
  //                       <td rowspan="${group.items.length}" style="border: 1.5px solid rgba(0, 0, 0, 0.12); white-space: pre-line; text-align: center;">
  //                         ${group.processing.LAMINATED ? group.processing.LAMINATED.split('\n').map((line, index) => 
  //                           index === 1 ? 
  //                             `<div style="color: #1976d2; padding: 4px 8px; border-radius: 4px; display: inline-block; margin-top: 4px; font-weight: normal;">${line}</div>` :
  //                             `${line}${index === 0 ? '<br>' : ''}`
  //                         ).join('') : ''}
  //                       </td>
  //                     ` : ''}
  //                     ${itemIndex === 0 ? `
  //                       <td rowspan="${group.items.length}" style="border: 1.5px solid rgba(0, 0, 0, 0.12); white-space: pre-line; text-align: center;">
  //                         ${group.processing.INSULATED_GLASS_UNIT ? group.processing.INSULATED_GLASS_UNIT.split('\n').map((line, index) => 
  //                           index === 1 ? 
  //                             `<div style="color: #1976d2; padding: 4px 8px; border-radius: 4px; display: inline-block; margin-top: 4px; font-weight: normal;">${line}</div>` :
  //                             `${line}${index === 0 ? '<br>' : ''}`
  //                         ).join('') : ''}
  //                       </td>
  //                     ` : ''}
  //                   </tr>
  //                 `).join('')
  //               ).join('');
  //             } else {
  //               return row.items.map((item, index) => `
  //                 <tr style="background-color: ${getRowBackgroundColor(row.type)};">
  //                   ${index === 0 ? `
  //                     <td rowspan="${row.items.length}" style="border: 1.5px solid rgba(0, 0, 0, 0.12);">${row.division1}</td>
  //                   ` : ''}
  //                   <td style="border: 1.5px solid rgba(0, 0, 0, 0.12);">${item.no}</td>
  //                   <td style="border: 1.5px solid rgba(0, 0, 0, 0.12);">${item.product1}</td>
  //                   <td style="border: 1.5px solid rgba(0, 0, 0, 0.12); text-align: center;">
  //                     <div style="color: #1976d2; padding: 4px 8px; border-radius: 4px; display: inline-block; font-weight: normal; text-align: center; width: 100%;">
  //                       ${item.product2}
  //                     </div>
  //                   </td>
  //                   ${index === 0 ? `
  //                     <td colspan="4" rowspan="${row.items.length}" style="border: 1.5px solid rgba(0, 0, 0, 0.12); text-align: center; color: rgba(0, 0, 0, 0.6); border-left: none; border-right: none;">
  //                       해당없음
  //                     </td>
  //                   ` : ''}
  //                 </tr>
  //               `).join('');
  //             }
  //           }
  //         }).join('')}
  //       </tbody>
  //     </table>
  //   `;

  //   const documentTableHtml = `
  //     <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px;">
  //       <thead>
  //         <tr>
  //           ${documentTableData.headers.map(header => `
  //             <th colspan="${header.colspan || 1}" rowspan="${header.rowspan || 1}" 
  //                 style="background-color: rgba(0, 0, 0, 0.45); color: white; font-weight: bold; text-align: center; border: 1.5px solid rgba(0, 0, 0, 0.12);">
  //               ${header.label}
  //             </th>
  //           `).join('')}
  //         </tr>
  //         <tr>
  //           ${documentTableData.headers.map(header => 
  //             header.subHeaders?.map(subHeader => `
  //               <th colspan="${subHeader.colspan || 1}" style="background-color: rgba(0, 0, 0, 0.45); color: white; font-weight: bold; text-align: center; border: 1.5px solid rgba(0, 0, 0, 0.12); width: ${subHeader.width};">
  //                 ${subHeader.label}
  //               </th>
  //             `).join('') || ''
  //           ).join('')}
  //         </tr>
  //       </thead>
  //       <tbody>
  //         ${(() => {
  //           const labelRowSpans = [];
  //           let prevLabel = null;
  //           let startIdx = 0;
  //           for (let i = 0; i < documentTableData.rows.length; i++) {
  //             if (documentTableData.rows[i].label !== prevLabel) {
  //               if (prevLabel !== null) {
  //                 labelRowSpans.push({ start: startIdx, span: i - startIdx });
  //               }
  //               prevLabel = documentTableData.rows[i].label;
  //               startIdx = i;
  //             }
  //           }
  //           if (prevLabel !== null) {
  //             labelRowSpans.push({ start: startIdx, span: documentTableData.rows.length - startIdx });
  //           }
  //           const labelRowspanMap = {};
  //           labelRowSpans.forEach(({ start, span }) => {
  //             labelRowspanMap[start] = span;
  //           });

  //           return documentTableData.rows.map((row, index) => {
  //             if (row.type === 'RAW_MATERIAL' && row.children) {
  //               return row.children.map((child, childIndex) => `
  //                 <tr>
  //                   ${childIndex === 0 && labelRowspanMap[index] ? `
  //                     <td rowspan="${labelRowspanMap[index]}" style="border: 1.5px solid rgba(0, 0, 0, 0.12);">${row.label}</td>
  //                   ` : ''}
  //                   ${childIndex === 0 && row.companyRowspan > 0 ? `
  //                     <td rowspan="${row.companyRowspan}" style="border: 1.5px solid rgba(0, 0, 0, 0.12);">${row.company}</td>
  //                   ` : ''}
  //                   ${childIndex === 0 && row.companyRowspan > 0 ? (
  //                     row.documentCells.map((cell, i) => (
  //                       <TableCell 
  //                         key={`general-${i}`} 
  //                         align={i === row.documentCells.length - 1 ? "left" : "center"} 
  //                         rowSpan={row.companyRowspan} 
  //                         sx={{ whiteSpace: 'pre-line' }}
  //                       >
  //                         {Array.isArray(cell) ? (
  //                           <Box sx={{ whiteSpace: 'pre-line', textAlign: 'left' }}>
  //                             {cell.map((doc, index) => (
  //                               <Typography 
  //                                 key={index} 
  //                                 variant="body2"
  //                                 sx={{ 
  //                                   textDecoration: doc.hasPath ? 'none' : 'line-through',
  //                                   color: doc.hasPath ? 'inherit' : 'text.secondary'
  //                                 }}
  //                               >
  //                                 • {doc.label}
  //                               </Typography>
  //                             ))}
  //                           </Box>
  //                         ) : (
  //                           <Typography
  //                             variant="body2"
  //                             sx={{
  //                               color: cell.hasPath ? 
  //                                 (cell.valid || cell.validYN ? 'success.main' : 'error.main') : 
  //                                 'text.secondary'
  //                             }}
  //                           >
  //                             {cell.hasPath ? 
  //                               (cell.valid || cell.validYN ? '●' : '▲') : 
  //                               (cell.required ? '×' : '-')}
  //                           </Typography>
  //                         )}
  //                       </TableCell>
  //                     ))
  //                   ) : null}
  //                   <td style="border: 1.5px solid rgba(0, 0, 0, 0.12);">
  //                     <div style="font-size: 0.875rem;">
  //                       ${child.label} ${row.productName.name}
  //                     </div>
  //                   </td>
  //                   <td style="border: 1.5px solid rgba(0, 0, 0, 0.12); text-align: center;">
  //                     ${row.productName.g04docuGCertificationName || '-'}
  //                   </td>
  //                   <td style="border: 1.5px solid rgba(0, 0, 0, 0.12); text-align: center;">
  //                     ${row.productName.hasCertificationDoc ? '●' : '×'}
  //                   </td>
  //                   <td style="border: 1.5px solid rgba(0, 0, 0, 0.12); text-align: center;">
  //                     ${row.productName.hasDoc ? 
  //                       (row.productName.isValid || row.productName.isValidYN ? '●' : '▲') :
  //                       (row.productName.hasPerformanceDoc ? '○' : '×')}
  //                   </td>
  //                   <td style="border: 1.5px solid rgba(0, 0, 0, 0.12); text-align: center;">
  //                     ${row.productName.hasComparisonDoc ? '●' : '-'}
  //                   </td>
  //                   <td style="border: 1.5px solid rgba(0, 0, 0, 0.12); text-align: center;">-</td>
  //                 </tr>
  //               `).join('');
  //             } else if ((row.type === 'SUB_MATERIAL_PROCESS' || row.type === 'SUB_MATERIAL_BUILD') && row.children) {
  //               return row.children.map((child, childIndex) => `
  //                 <tr>
  //                   ${childIndex === 0 && labelRowspanMap[index] ? `
  //                     <td rowspan="${labelRowspanMap[index]}" style="border: 1.5px solid rgba(0, 0, 0, 0.12);">${row.label}</td>
  //                   ` : ''}
  //                   ${childIndex === 0 && row.companyRowspan > 0 ? `
  //                     <td rowspan="${row.companyRowspan}" style="border: 1.5px solid rgba(0, 0, 0, 0.12);">${row.company}</td>
  //                   ` : ''}
  //                   ${childIndex === 0 && row.companyRowspan > 0 ? (
  //                     row.documentCells.map((cell, i) => (
  //                       <TableCell 
  //                         key={`general-${i}`} 
  //                         align={i === row.documentCells.length - 1 ? "left" : "center"} 
  //                         rowSpan={row.companyRowspan} 
  //                         sx={{ whiteSpace: 'pre-line' }}
  //                       >
  //                         {Array.isArray(cell) ? (
  //                           <Box sx={{ whiteSpace: 'pre-line', textAlign: 'left' }}>
  //                             {cell.map((doc, index) => (
  //                               <Typography 
  //                                 key={index} 
  //                                 variant="body2"
  //                                 sx={{ 
  //                                   textDecoration: doc.hasPath ? 'none' : 'line-through',
  //                                   color: doc.hasPath ? 'inherit' : 'text.secondary'
  //                                 }}
  //                               >
  //                                 • {doc.label}
  //                               </Typography>
  //                             ))}
  //                           </Box>
  //                         ) : (
  //                           <Typography
  //                             variant="body2"
  //                             sx={{
  //                               color: cell.hasPath ? 
  //                                 (cell.valid || cell.validYN ? 'success.main' : 'error.main') : 
  //                                 'text.secondary'
  //                             }}
  //                           >
  //                             {cell.hasPath ? 
  //                               (cell.valid || cell.validYN ? '●' : '▲') : 
  //                               (cell.required ? '×' : '-')}
  //                           </Typography>
  //                         )}
  //                       </TableCell>
  //                     ))
  //                   ) : null}
  //                   <td style="border: 1.5px solid rgba(0, 0, 0, 0.12);">
  //                     <div style="font-size: 0.875rem; font-weight: bold;">
  //                       ${row.productName.name}
  //                     </div>
  //                     <div style="font-size: 0.875rem; padding-left: 16px; color: rgba(0, 0, 0, 0.6);">
  //                       • ${child.label}
  //                     </div>
  //                   </td>
  //                   <td style="border: 1.5px solid rgba(0, 0, 0, 0.12); text-align: center;">
  //                     ${row.productName.g04docuGCertificationName || '-'}
  //                   </td>
  //                   <td style="border: 1.5px solid rgba(0, 0, 0, 0.12); text-align: center;">
  //                     ${row.productName.hasCertificationDoc ? '●' : '×'}
  //                   </td>
  //                   <td style="border: 1.5px solid rgba(0, 0, 0, 0.12); text-align: center;">
  //                     ${row.productName.hasDoc ? 
  //                       (row.productName.isValid || row.productName.isValidYN ? '●' : '▲') :
  //                       (row.productName.hasPerformanceDoc ? '○' : '×')}
  //                   </td>
  //                   <td style="border: 1.5px solid rgba(0, 0, 0, 0.12); text-align: center;">
  //                     ${row.productName.hasComparisonDoc ? '●' : '-'}
  //                   </td>
  //                   <td style="border: 1.5px solid rgba(0, 0, 0, 0.12); text-align: center;">-</td>
  //                 </tr>
  //               `).join('');
  //             } else {
  //               return `
  //                 <tr>
  //                   ${labelRowspanMap[index] ? `
  //                     <td rowspan="${labelRowspanMap[index]}" style="border: 1.5px solid rgba(0, 0, 0, 0.12);">${row.label}</td>
  //                   ` : ''}
  //                   ${row.companyRowspan > 0 ? `
  //                     <td rowspan="${row.companyRowspan}" style="border: 1.5px solid rgba(0, 0, 0, 0.12);">${row.company}</td>
  //                   ` : ''}
  //                   ${row.companyRowspan > 0 ? (
  //                     row.documentCells.map((cell, i) => (
  //                       <TableCell 
  //                         key={`general-${i}`} 
  //                         align={i === row.documentCells.length - 1 ? "left" : "center"} 
  //                         rowSpan={row.companyRowspan} 
  //                         sx={{ whiteSpace: 'pre-line' }}
  //                       >
  //                         {Array.isArray(cell) ? (
  //                           <Box sx={{ whiteSpace: 'pre-line', textAlign: 'left' }}>
  //                             {cell.map((doc, index) => (
  //                               <Typography 
  //                                 key={index} 
  //                                 variant="body2"
  //                                 sx={{ 
  //                                   textDecoration: doc.hasPath ? 'none' : 'line-through',
  //                                   color: doc.hasPath ? 'inherit' : 'text.secondary'
  //                                 }}
  //                               >
  //                                 • {doc.label}
  //                               </Typography>
  //                             ))}
  //                           </Box>
  //                         ) : (
  //                           <Typography
  //                             variant="body2"
  //                             sx={{
  //                               color: cell.hasPath ? 
  //                                 (cell.valid || cell.validYN ? 'success.main' : 'error.main') : 
  //                                 'text.secondary'
  //                             }}
  //                           >
  //                             {cell.hasPath ? 
  //                               (cell.valid || cell.validYN ? '●' : '▲') : 
  //                               (cell.required ? '×' : '-')}
  //                           </Typography>
  //                         )}
  //                       </TableCell>
  //                     ))
  //                   ) : null}
  //                   ${row.label === '시공업체' ? `
  //                     <td colspan="6" rowspan="${row.companyRowspan}" style="border: 1.5px solid rgba(0, 0, 0, 0.12); text-align: center;">
  //                       해당없음
  //                     </td>
  //                   ` : `
  //                     <td style="border: 1.5px solid rgba(0, 0, 0, 0.12);">
  //                       <div style="font-size: 0.875rem;">
  //                         ${row.productName.name}
  //                       </div>
  //                     </td>
  //                     ${(() => {
  //                       const currentIndex = documentTableData.rows.findIndex(r => r.id === row.id);
  //                       const sameCertificationRows = [];
  //                       let i = currentIndex;
                        
  //                       while (i < documentTableData.rows.length && 
  //                              documentTableData.rows[i].productName?.g04docuGCertificationName === row.productName?.g04docuGCertificationName) {
  //                         sameCertificationRows.push(documentTableData.rows[i]);
  //                         i++;
  //                       }
                        
  //                       if (currentIndex === 0 || 
  //                           documentTableData.rows[currentIndex - 1].productName?.g04docuGCertificationName !== row.productName?.g04docuGCertificationName) {
  //                         return (
  //                           <>
  //                             <TableCell align="center" rowSpan={sameCertificationRows.length} sx={{ whiteSpace: 'pre-line' }}>
  //                               {row.productName?.g04docuGCertificationName ? (
  //                                 <>
  //                                   <Typography variant="body2" sx={{ color: 'text.secondary' }}>
  //                                     {row.productName.g04docuGCertificationName.match(/\[(.*?)\]/)?.[1] || ''}
  //                                   </Typography>
  //                                   <Typography variant="body2">
  //                                     {row.productName.g04docuGCertificationName.replace(/\[.*?\]/, '').trim()}
  //                                   </Typography>
  //                                 </>
  //                               ) : '-'}
  //                             </TableCell>
  //                             <TableCell align="center" rowSpan={sameCertificationRows.length}>
  //                               {row.productName?.hasCertificationDoc ? '●' : '×'}
  //                             </TableCell>
  //                           </>
  //                         );
  //                       }
  //                       return null;
  //                     })()}
  //                     <td style="border: 1.5px solid rgba(0, 0, 0, 0.12); text-align: center;">
  //                       ${row.productName.hasDoc ? 
  //                         (row.productName.isValid || row.productName.isValidYN ? '●' : '▲') :
  //                         (row.productName.hasPerformanceDoc ? '○' : '×')}
  //                     </td>
  //                     <td style="border: 1.5px solid rgba(0, 0, 0, 0.12); text-align: center;">
  //                       ${row.productName.hasComparisonDoc ? '●' : '-'}
  //                     </td>
  //                     {(() => {
  //                       const currentIndex = documentTableData.rows.findIndex(r => r.id === row.id);
  //                       const sameCompanyRows = [];
  //                       let i = currentIndex;
                        
  //                       while (i < documentTableData.rows.length && 
  //                              documentTableData.rows[i].company === row.company) {
  //                         sameCompanyRows.push(documentTableData.rows[i]);
  //                         i++;
  //                       }
                        
  //                       if (currentIndex === 0 || 
  //                           documentTableData.rows[currentIndex - 1].company !== row.company) {
  //                         return (
  //                           <TableCell align="center" rowSpan={sameCompanyRows.length}>
  //                             {row.etcDocs && row.etcDocs.length > 0 ? (
  //                               <Box sx={{ whiteSpace: 'pre-line', textAlign: 'left' }}>
  //                                 {row.etcDocs.map((doc, index) => (
  //                                   <Typography 
  //                                     key={index} 
  //                                     variant="body2"
  //                                     sx={{ 
  //                                       textDecoration: doc.hasPath ? 'none' : 'line-through',
  //                                       color: doc.hasPath ? 'inherit' : 'text.secondary',
  //                                       // 기타 문서의 경우 첨부 상태만 하이라이트
  //                                       ...(legendHover === '첨부' && doc.hasPath && { backgroundColor: 'rgba(25, 118, 210, 0.12)' })
  //                                     }}
  //                                   >
  //                                     • {doc.label}
  //                                   </Typography> 
  //                                 ))}
  //                               </Box>
  //                             ) : '-'}
  //                           </TableCell>
  //                         );
  //                       }
  //                       return null;
  //                     })()}
  //                   `}
  //                 </tr>
  //               `;
  //             }
  //           }).join('');
  //         })()}
  //       </tbody>
  //     </table>
  //   `;

  //   // HTML 생성 후 콜백 호출
  //   if (onTableHtmlGenerated) {
  //     onTableHtmlGenerated({
  //       materialTableHtml,
  //       documentTableHtml
  //     });
  //   }
  // };

  // 컴포넌트 마운트 시 HTML 생성
  // React.useEffect(() => {
  //   generateTableHtml();
  // }, [materialTableData, documentTableData]);

  return (
    <Box sx={{ width: '100%', overflow: 'auto' }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Accordion expanded={materialExpanded} onChange={() => setMaterialExpanded(!materialExpanded)}>
            <AccordionSummary
              aria-controls="basic-content"
              id="basic-header"
              sx={{
                minHeight: '38px', // 헤더의 최소 높이 설정
                '& .MuiAccordionSummary-content': {
                  margin: '4px 0', // 내용의 상하 마진 조정
                },
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h6" component="div" sx={{ ml: 1}}>{"1. 자재 정보"}</Typography>
                </Grid>
              </Grid>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer component={Paper} elevation={0} sx={{ mb: 2, boxShadow: 'none' }}>
                <Table 
                  size="small"
                  sx={{
                    borderCollapse: 'collapse',
                    border: '1px solid rgba(0, 0, 0, 0.12) !important',
                    '& th, & td': {
                      border: '1px solid rgba(0, 0, 0, 0.12) !important',
                    }
                  }}
                >
                  <TableHead>
                    <TableRow>
                      {materialTableData.headers.map((header, index) => (
                        <TableCell
                          key={index}
                          colSpan={header.colspan}
                          rowSpan={header.rowspan}
                          align="center"
                          sx={{
                            backgroundColor: getBackgroundColor(header.type),
                            color: 'primary.contrastText',
                            fontWeight: 'bold',
                            ...(header.subHeaders ? {} : { width: header.width })
                          }}
                        >
                          {header.label}
                        </TableCell>
                      ))}
                    </TableRow>
                    <TableRow>
                      {materialTableData.headers.map((header, index) => (
                        header.subHeaders?.map((subHeader, subIndex) => (
                          <TableCell
                            key={`${index}-${subIndex}`}
                            align="center"
                            sx={{
                              backgroundColor: getBackgroundColor(header.type),
                              color: 'primary.contrastText',
                              fontWeight: 'bold',
                              width: subHeader.width
                            }}
                          >
                            {subHeader.label}
                          </TableCell>
                        ))
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {materialTableData.rows.map((row) => {
                      if (row.items) {
                        if (row.type === 'RAW_MATERIAL') {
                          // no와 product1이 같은 항목들을 그룹화
                          const groupedItems = row.items.reduce((acc, item) => {
                            const key = `${item.no}-${item.product1}`;
                            if (!acc[key]) {
                              acc[key] = {
                                items: [item],
                                no: item.no,
                                product1: item.product1,
                                processing: { ...item.processing }
                              };
                            } else {
                              acc[key].items.push(item);
                              // processing 객체의 각 키에 대해 값이 있으면 추가
                              if (item.processing.LAMINATED) {
                                acc[key].processing.LAMINATED = item.processing.LAMINATED;
                              }
                              if (item.processing.INSULATED_GLASS_UNIT) {
                                acc[key].processing.INSULATED_GLASS_UNIT = item.processing.INSULATED_GLASS_UNIT;
                              }
                            }
                            return acc;
                          }, {});

                          return Object.values(groupedItems).map((group, groupIndex) => {
                            return group.items.map((item, itemIndex) => (
                              <TableRow key={`${item.id}-${itemIndex}`} sx={{ backgroundColor: getRowBackgroundColor(row.type) }}>
                                {groupIndex === 0 && itemIndex === 0 ? (
                                  <TableCell rowSpan={row.items.length} colSpan={2}>{row.division1}</TableCell>
                                ) : null}
                                {itemIndex === 0 ? (
                                  <TableCell rowSpan={group.items.length}>{group.product1.replaceAll("|", " ").replaceAll("+", " + ")}</TableCell>
                                ) : null}
                                <TableCell sx={{ whiteSpace: 'pre-line' }} align="center">
                                  {item.product2.split('\n').map((line, index) => (
                                    index === 1 ? (
                                      <Box key={index} sx={{ 
                                        color: 'primary.main',
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        display: 'inline-block',
                                        marginTop: '4px',
                                        fontWeight: 'normal'
                                      }}>
                                        {line}
                                      </Box>
                                    ) : (
                                      <React.Fragment key={index}>
                                        {line.replace(/\(.*?\)/, "")}
                                        {index === 0 && <br />}
                                      </React.Fragment>
                                    )
                                  ))}
                                </TableCell>
                                <TableCell sx={{ whiteSpace: 'pre-line' }} align="center">
                                  {item.processing.HEAT_STRENGTHENED ? (
                                    item.processing.HEAT_STRENGTHENED.split('\n').map((line, index) => (
                                      index === 1 ? (
                                        <Box key={index} sx={{ 
                                          color: 'primary.main',
                                          padding: '4px 8px',
                                          borderRadius: '4px',
                                          display: 'inline-block',
                                          marginTop: '4px',
                                          fontWeight: 'normal'
                                        }}>
                                          {line}
                                        </Box>
                                      ) : (
                                        <React.Fragment key={index}>
                                          {line}
                                          {index === 0 && <br />}
                                        </React.Fragment>
                                      )
                                    ))
                                  ) : null}
                                </TableCell>
                                <TableCell sx={{ whiteSpace: 'pre-line' }} align="center">
                                  {item.processing.TEMPERED ? (
                                    item.processing.TEMPERED.split('\n').map((line, index) => (
                                      index === 1 ? (
                                        <Box key={index} sx={{ 
                                          color: 'primary.main',
                                          padding: '4px 8px',
                                          borderRadius: '4px',
                                          display: 'inline-block',
                                          marginTop: '4px',
                                          fontWeight: 'normal'
                                        }}>
                                          {line}
                                        </Box>
                                      ) : (
                                        <React.Fragment key={index}>
                                          {line}
                                          {index === 0 && <br />}
                                        </React.Fragment>
                                      )
                                    ))
                                  ) : null}
                                </TableCell>
                                {itemIndex === 0 ? (
                                  <TableCell 
                                    sx={{ 
                                      whiteSpace: 'pre-line',
                                    }} 
                                    align="center" 
                                    rowSpan={group.items.length}
                                  >
                                    {group.processing.LAMINATED ? (
                                      group.processing.LAMINATED.split('\n').map((line, index) => (
                                        index === 1 ? (
                                          <Box key={index} sx={{ 
                                            color: 'primary.main',
                                            padding: '4px 8px',
                                            borderRadius: '4px',
                                            display: 'inline-block',
                                            marginTop: '4px',
                                            fontWeight: 'normal'
                                          }}>
                                            {line}
                                          </Box>
                                        ) : (
                                          <React.Fragment key={index}>
                                            {line}
                                            {index === 0 && <br />}
                                          </React.Fragment>
                                        )
                                      ))
                                    ) : null}
                                  </TableCell>
                                ) : null}
                                {itemIndex === 0 ? (
                                  <TableCell 
                                    sx={{ 
                                      whiteSpace: 'pre-line',
                                    }} 
                                    align="center" 
                                    rowSpan={group.items.length}
                                  >
                                    {group.processing.INSULATED_GLASS_UNIT ? (
                                      group.processing.INSULATED_GLASS_UNIT.split('\n').map((line, index) => (
                                        index === 1 ? (
                                          <Box key={index} sx={{ 
                                            color: 'primary.main',
                                            padding: '4px 8px',
                                            borderRadius: '4px',
                                            display: 'inline-block',
                                            marginTop: '4px',
                                            fontWeight: 'normal'
                                          }}>
                                            {line}
                                          </Box>
                                        ) : (
                                          <React.Fragment key={index}>
                                            {line}
                                            {index === 0 && <br />}
                                          </React.Fragment>
                                        )
                                      ))
                                    ) : null}
                                  </TableCell>
                                ) : null}
                              </TableRow>
                            ));
                          }).flat();
                        } else {
                          return row.items.map((item, index) => (
                            <TableRow key={item.id} sx={{ backgroundColor: getRowBackgroundColor(row.type) }}>
                              {index === 0 ? (
                                <TableCell rowSpan={row.items.length}>{row.division1}</TableCell>
                              ) : null}
                              <TableCell>{item.no}</TableCell>
                              <TableCell>{item.product1}</TableCell>
                              <TableCell align="center">
                                <Box sx={{ 
                                  color: 'primary.main',
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  display: 'inline-block',
                                  fontWeight: 'normal',
                                  textAlign: 'center',
                                  width: '100%'
                                }}>
                                  {item.product2}
                                </Box>
                              </TableCell>
                              {index === 0 ? (
                                <TableCell colSpan={4} rowSpan={row.items.length} align="center" sx={{ color: 'text.secondary' }}>
                                  해당없음
                                </TableCell>
                              ) : null}
                            </TableRow>
                          ));
                        }
                      }
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        </Grid>
        <Grid item xs={12}>
          <Accordion expanded={documentExpanded} onChange={() => setDocumentExpanded(!documentExpanded)}>
            <AccordionSummary
              aria-controls="basic-content"
              id="basic-header"
              sx={{
                minHeight: '38px', // 헤더의 최소 높이 설정
                '& .MuiAccordionSummary-content': {
                  margin: '4px 0', // 내용의 상하 마진 조정
                },
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="h6" component="div" sx={{ ml: 1}}>{"2. 공급업체별 첨부서류"}</Typography>
                </Grid>
                <Grid item xs={12} sm={8} display="flex" justifyContent="flex-end" alignItems="center">
                  <Stack direction="row" spacing={1}>
                    <Box
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1, 
                        padding: '4px 8px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        transform: 'translateZ(0)',
                        backgroundColor: selectedLegends.includes('첨부') ? 'rgba(25, 118, 210, 0.12)' : 'transparent',
                        '&:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.1)',
                          boxShadow: '0 0 8px rgba(25, 118, 210, 0.3)',
                          transform: 'translateZ(0)',
                          '& .MuiTypography-root': {
                            color: '#2EA2DE',
                            textShadow: '0 0 2px rgba(25, 118, 210, 0.3)'
                          }
                        }
                      }}
                      onClick={(e) => handleLegendClick(e, '첨부')}
                      onMouseEnter={() => handleLegendHover('첨부')}
                      onMouseLeave={handleLegendLeave}
                    >
                      <Typography variant="body2" sx={{ color: selectedLegends.includes('첨부') ? '#2EA2DE' : 'inherit' }}>●</Typography>
                      <Typography variant="body2" sx={{ color: selectedLegends.includes('첨부') ? '#2EA2DE' : 'inherit' }}>첨부</Typography>
                    </Box>
                    <Box
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1, 
                        padding: '4px 8px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        transform: 'translateZ(0)',
                        backgroundColor: selectedLegends.includes('만료') ? 'rgba(255, 0, 0, 0.08)' : 'transparent',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 0, 0, 0.08)',
                          boxShadow: '0 0 8px rgba(255, 0, 0, 0.3)',
                          transform: 'translateZ(0)',
                          '& .MuiTypography-root': {
                            color: '#DB4E2E',
                            textShadow: '0 0 2px rgba(255, 0, 0, 0.3)'
                          }
                        }
                      }}
                      onClick={(e) => handleLegendClick(e, '만료')}
                      onMouseEnter={() => handleLegendHover('만료')}
                      onMouseLeave={handleLegendLeave}
                    >
                      <Typography variant="body2" sx={{ color: selectedLegends.includes('만료') ? '#DB4E2E' : 'inherit' }}>▲</Typography>
                      <Typography variant="body2" sx={{ color: selectedLegends.includes('만료') ? '#DB4E2E' : 'inherit' }}>만료</Typography>
                    </Box>
                    <Box
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1, 
                        padding: '4px 8px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        transform: 'translateZ(0)',
                        backgroundColor: selectedLegends.includes('성능확인서') ? 'rgba(25, 118, 210, 0.25)' : 'transparent',
                        '&:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.25)',
                          boxShadow: '0 0 8px rgba(25, 118, 210, 0.3)',
                          transform: 'translateZ(0)',
                          '& .MuiTypography-root': {
                            color: '#1976d2',
                            textShadow: '0 0 2px rgba(25, 118, 210, 0.3)'
                          }
                        }
                      }}
                      onClick={(e) => handleLegendClick(e, '성능확인서')}
                      onMouseEnter={() => handleLegendHover('성능확인서')}
                      onMouseLeave={handleLegendLeave}
                    >
                      <Typography variant="body2" sx={{ color: selectedLegends.includes('성능확인서') ? '#1976d2' : 'inherit' }}>○</Typography>
                      <Typography variant="body2" sx={{ color: selectedLegends.includes('성능확인서') ? '#1976d2' : 'inherit' }}>성능확인서</Typography>
                    </Box>
                    <Box
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1, 
                        padding: '4px 8px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        transform: 'translateZ(0)',
                        backgroundColor: selectedLegends.includes('없음(필수)') ? 'rgba(100, 100, 100, 0.25)' : 'transparent',
                        '&:hover': {
                          backgroundColor: 'rgba(100, 100, 100, 0.25)',
                          boxShadow: '0 0 8px rgba(100, 100, 100, 0.4)',
                          transform: 'translateZ(0)',
                          '& .MuiTypography-root': {
                            color: '#444444',
                            textShadow: '0 0 2px rgba(100, 100, 100, 0.4)'
                          }
                        }
                      }}
                      onClick={(e) => handleLegendClick(e, '없음(필수)')}
                      onMouseEnter={() => handleLegendHover('없음(필수)')}
                      onMouseLeave={handleLegendLeave}
                    >
                      <Typography variant="body2" sx={{ color: selectedLegends.includes('없음(필수)') ? '#444444' : 'inherit' }}>×</Typography>
                      <Typography variant="body2" sx={{ color: selectedLegends.includes('없음(필수)') ? '#444444' : 'inherit' }}>없음(필수)</Typography>
                    </Box>
                    <Box
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1, 
                        padding: '4px 8px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        transform: 'translateZ(0)',
                        backgroundColor: selectedLegends.includes('없음(선택)') ? 'rgba(100, 100, 100, 0.10)' : 'transparent',
                        '&:hover': {
                          backgroundColor: 'rgba(100, 100, 100, 0.10)',
                          boxShadow: '0 0 8px rgba(100, 100, 100, 0.3)',
                          transform: 'translateZ(0)',
                          '& .MuiTypography-root': {
                            color: '#666666',
                            textShadow: '0 0 2px rgba(100, 100, 100, 0.3)'
                          }
                        }
                      }}
                      onClick={(e) => handleLegendClick(e, '없음(선택)')}
                      onMouseEnter={() => handleLegendHover('없음(선택)')}
                      onMouseLeave={handleLegendLeave}
                    >
                      <Typography variant="body2" sx={{ color: selectedLegends.includes('없음(선택)') ? '#666666' : 'inherit' }}>-</Typography>
                      <Typography variant="body2" sx={{ color: selectedLegends.includes('없음(선택)') ? '#666666' : 'inherit' }}>없음(선택)</Typography>
                    </Box>
                  </Stack>
                </Grid>
              </Grid>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer component={Paper} elevation={0} sx={{ mb: 2, boxShadow: 'none' }}>
                <Table 
                  size="small"
                  sx={{
                    borderCollapse: 'collapse',
                    border: '1px solid rgba(0, 0, 0, 0.12) !important',
                    '& th, & td': {
                      border: '1px solid rgba(0, 0, 0, 0.12) !important',
                    }
                  }}
                >
                  <TableHead>
                    <TableRow>
                      {documentTableData.headers.map((header, index) => (
                        <TableCell
                          key={index}
                          colSpan={header.colspan}
                          rowSpan={header.rowspan}
                          align="center"
                          sx={{
                            backgroundColor: getBackgroundColor(header.type),
                            color: 'primary.contrastText',
                            fontWeight: 'bold',
                            ...(header.subHeaders ? {} : { width: header.width })
                          }}
                        >
                          {header.label}
                        </TableCell>
                      ))}
                    </TableRow>
                    <TableRow>
                      {documentTableData.headers.map((header, index) => (
                        header.subHeaders?.map((subHeader, subIndex) => (
                          <TableCell
                            key={`${index}-${subIndex}`}
                            align="center"
                            colSpan={subHeader.colspan}
                            sx={{
                              backgroundColor: getBackgroundColor(header.type),
                              color: 'primary.contrastText',
                              fontWeight: 'bold',
                              width: subHeader.width,
                              whiteSpace: 'pre-line'
                            }}
                          >
                            {subHeader.label}
                          </TableCell>
                        ))
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(() => {
                      // label 병합을 위한 사전 처리
                      const labelRowSpans = [];
                      let prevLabel = null;
                      let startIdx = 0;
                      for (let i = 0; i < documentTableData.rows.length; i++) {
                        if (documentTableData.rows[i].label !== prevLabel) {
                          if (prevLabel !== null) {
                            labelRowSpans.push({ start: startIdx, span: i - startIdx });
                          }
                          prevLabel = documentTableData.rows[i].label;
                          startIdx = i;
                        }
                      }
                      if (prevLabel !== null) {
                        labelRowSpans.push({ start: startIdx, span: documentTableData.rows.length - startIdx });
                      }
                      // 각 행별로 labelRowspan 값 부여
                      const labelRowspanMap = {};
                      labelRowSpans.forEach(({ start, span }) => {
                        labelRowspanMap[start] = span;
                      });

                      return documentTableData.rows.map((row, index) => {
                        // console.log('row', row);
                        // console.log('row.type', row.type);
                        // console.log('row.children', row.children);
                        // // 원자재의 경우 children을 별도 행으로 분리
                        // if (row.type === 'RAW_MATERIAL' && row.children) {
                        //   return row.children.map((child, childIndex) => (
                        //     <TableRow key={`${row.id}-${childIndex}`}>
                        //       {childIndex === 0 && labelRowspanMap[index] ? (
                        //         <TableCell rowSpan={labelRowspanMap[index]}>{row.label}</TableCell>
                        //       ) : null}
                        //       {childIndex === 0 && row.companyRowspan > 0 ? (
                        //         <TableCell rowSpan={row.companyRowspan}>{row.company}</TableCell>
                        //       ) : null}
                        //       {/* 일반서류 컬럼 */}
                        //       {childIndex === 0 && row.companyRowspan > 0 ? (
                        //         row.documentCells.map((cell, i) => (
                        //           <TableCell 
                        //             key={`general-${i}`} 
                        //             align={i === row.documentCells.length - 1 ? "left" : "center"} 
                        //             rowSpan={row.companyRowspan} 
                        //             sx={{ whiteSpace: 'pre-line' }}
                        //           >
                        //             {cell}
                        //           </TableCell>
                        //         ))
                        //       ) : null}
                        //       {/* 기술문서 컬럼 */}
                        //       <TableCell>
                        //         <Typography variant="body2">
                        //           {child.label} {row.productName.name}
                        //         </Typography>
                        //       </TableCell>
                        //       <TableCell align="center" sx={{ whiteSpace: 'pre-line' }}>
                        //         {row.productName.g04docuGCertificationName || '-'}
                        //       </TableCell>
                        //       <TableCell align="center">
                        //         {row.productName.hasCertificationDoc ? '●' : '×'}
                        //       </TableCell>
                        //       <TableCell align="center">
                        //         {row.productName.hasDoc ? 
                        //           (row.productName.isValid || row.productName.isValidYN ? '●' : '▲') :
                        //           (row.productName.hasPerformanceDoc ? '○' : '×')}
                        //       </TableCell>
                        //       {(() => {
                        //         const currentIndex = documentTableData.rows.findIndex(r => r.id === row.id);
                        //         const sameCompanyRows = [];
                        //         let i = currentIndex;
                                
                        //         while (i < documentTableData.rows.length && 
                        //                documentTableData.rows[i].company === row.company) {
                        //           sameCompanyRows.push(documentTableData.rows[i]);
                        //           i++;
                        //         }
                                
                        //         if (currentIndex === 0 || 
                        //             documentTableData.rows[currentIndex - 1].company !== row.company) {
                        //           return (
                        //             <TableCell align="center" rowSpan={sameCompanyRows.length}>-</TableCell>
                        //           );
                        //         }
                        //         return null;
                        //       })()}
                        //     </TableRow>
                        //   ));
                        // }
                        // // 가공부자재와 시공부자재의 경우
                        // else if ((row.type === 'SUB_MATERIAL_PROCESS' || row.type === 'SUB_MATERIAL_BUILD') && row.children) {
                        //   return row.children.map((child, childIndex) => (
                        //     <TableRow key={`${row.id}-${childIndex}`}>
                        //       {childIndex === 0 && labelRowspanMap[index] ? (
                        //         <TableCell rowSpan={labelRowspanMap[index]}>{row.label}</TableCell>
                        //       ) : null}
                        //       {childIndex === 0 && row.companyRowspan > 0 ? (
                        //         <TableCell rowSpan={row.companyRowspan}>{row.company}</TableCell>
                        //       ) : null}
                        //       {/* 일반서류 컬럼 */}
                        //       {childIndex === 0 && row.companyRowspan > 0 ? (
                        //         row.documentCells.map((cell, i) => (
                        //           <TableCell 
                        //             key={`general-${i}`} 
                        //             align={i === row.documentCells.length - 1 ? "left" : "center"} 
                        //             rowSpan={row.companyRowspan} 
                        //             sx={{ whiteSpace: 'pre-line' }}
                        //           >
                        //             {cell}
                        //           </TableCell>
                        //         ))
                        //       ) : null}
                        //       {/* 기술문서 컬럼 */}
                        //       <TableCell>
                        //         <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        //           {row.productName.name}
                        //         </Typography>
                        //       </TableCell>
                        //       <TableCell align="center" sx={{ whiteSpace: 'pre-line' }}>
                        //         {row.productName.g04docuGCertificationName || '-'}
                        //       </TableCell>
                        //       <TableCell align="center">
                        //         {row.productName.hasCertificationDoc ? '●' : '×'}
                        //       </TableCell>
                        //       <TableCell align="center">
                        //         {row.productName.hasDoc ? 
                        //           (row.productName.isValid || row.productName.isValidYN ? '●' : '▲') :
                        //           (row.productName.hasPerformanceDoc ? '○' : '×')}
                        //       </TableCell>
                        //       {(() => {
                        //         const currentIndex = documentTableData.rows.findIndex(r => r.id === row.id);
                        //         const sameCompanyRows = [];
                        //         let i = currentIndex;
                                
                        //         while (i < documentTableData.rows.length && 
                        //                documentTableData.rows[i].company === row.company) {
                        //           sameCompanyRows.push(documentTableData.rows[i]);
                        //           i++;
                        //         }
                                
                        //         if (currentIndex === 0 || 
                        //             documentTableData.rows[currentIndex - 1].company !== row.company) {
                        //           return (
                        //             <TableCell align="center" rowSpan={sameCompanyRows.length}>-</TableCell>
                        //           );
                        //         }
                        //         return null;
                        //       })()}
                        //     </TableRow>
                        //   ));
                        // }
                        // 기본 행
                        return (
                          <TableRow key={row.id}>
                            {labelRowspanMap[index] ? (
                              <TableCell rowSpan={labelRowspanMap[index]} align="center">
                                {row.label.split('\n').map((line, index) => (
                                  <React.Fragment key={index}>
                                    {line}
                                    {index === 0 && <br />}
                                  </React.Fragment>
                                ))}
                              </TableCell>
                            ) : null}
                            {row.companyRowspan > 0 ? (
                              <TableCell rowSpan={row.companyRowspan}>{row.company}</TableCell>
                            ) : null}
                            {/* 일반서류 컬럼 */}
                            {row.companyRowspan > 0 ? (
                              row.documentCells.map((cell, i) => (
                                <TableCell 
                                  key={`general-${i}`} 
                                  align={i === row.documentCells.length - 1 && Array.isArray(cell) && cell.length > 0 ? "left" : "center"} 
                                  rowSpan={row.companyRowspan} 
                                  sx={{ 
                                    whiteSpace: 'pre-line',
                                    // 첨부: hasPath가 true이고 valid/validYN이 true인 경우
                                    ...((selectedLegends.includes('첨부') || legendHover === '첨부') && cell.hasPath && (cell.valid === true || cell.validYN === true) && { color: '#2EA2DE', backgroundColor: 'rgba(25, 118, 210, 0.12)' }),
                                    // 만료: hasPath가 true이지만 valid/validYN이 false인 경우
                                    ...((selectedLegends.includes('만료') || legendHover === '만료') && cell.hasPath && (cell.valid === false && cell.validYN === false) && { color: '#DB4E2E', backgroundColor: 'rgba(255, 0, 0, 0.08)' }),
                                    // 없음(필수): hasPath가 false이고 required가 true인 경우
                                    ...((selectedLegends.includes('없음(필수)') || legendHover === '없음(필수)') && !cell.hasPath && cell.required === true && { backgroundColor: 'rgba(100, 100, 100, 0.25)' }),
                                    // 없음(선택): hasPath가 false이고 required가 false인 경우
                                    ...((selectedLegends.includes('없음(선택)') || legendHover === '없음(선택)') && !cell.hasPath && cell.required === false && { backgroundColor: 'rgba(100, 100, 100, 0.10)' }),
                                  }}
                                >
                                  {(() => {
                                    console.log('Cell data:', { cell, legendHover, hasPath: cell.hasPath, valid: cell.valid, validYN: cell.validYN, required: cell.required });
                                    return null;
                                  })()}
                                  {Array.isArray(cell) ? (
                                    <Box sx={{ whiteSpace: 'pre-line', textAlign: 'left' }}>
                                      {cell.map((doc, index) => (
                                        doc.hasPath ? ( 
                                          <Typography 
                                            key={index} 
                                            variant="body2"
                                            sx={{
                                              color: doc.hasPath ? 'inherit' : 'text.secondary',
                                              // 기타 문서의 경우 첨부 상태만 하이라이트
                                              ...((selectedLegends.includes('첨부') || legendHover === '첨부') && doc.hasPath && { color: '#2EA2DE', backgroundColor: 'rgba(25, 118, 210, 0.12)' })
                                            }}
                                          >
                                            • {doc.label}
                                          </Typography> 
                                        ) : null
                                      ))}
                                    </Box>
                                  ) : (
                                    <Typography variant="body2">
                                      {cell.hasPath ? 
                                        (cell.valid || cell.validYN ? '●' : '▲') : 
                                        (cell.required ? '×' : '-')}
                                    </Typography>
                                  )}
                                </TableCell>
                              ))
                            ) : null}
                            {/* 기술문서 컬럼 */}
                            {row.label === '시공업체' ? (
                              <>
                                <TableCell align="center" colSpan={6} rowspan={row.companyRowspan}>
                                  해당없음
                                </TableCell>
                              </>
                            ) : (
                              <>
                                <TableCell>
                                  <Typography variant="body2">
                                    {row.productName.name}
                                  </Typography>
                                </TableCell>
                                {(() => {
                                  const currentIndex = documentTableData.rows.findIndex(r => r.id === row.id);
                                  const sameCertificationRows = [];
                                  let i = currentIndex;
                                  
                                  while (i < documentTableData.rows.length && 
                                        documentTableData.rows[i].productName?.g04docuGCertificationName === row.productName?.g04docuGCertificationName) {
                                    sameCertificationRows.push(documentTableData.rows[i]);
                                    i++;
                                  }
                                  
                                  if (currentIndex === 0 || 
                                      documentTableData.rows[currentIndex - 1].productName?.g04docuGCertificationName !== row.productName?.g04docuGCertificationName) {
                                    return (
                                      <>
                                        <TableCell align="center" rowSpan={sameCertificationRows.length} sx={{ whiteSpace: 'pre-line' }}>
                                          {row.productName?.g04docuGCertificationName || '-'}
                                        </TableCell>
                                        <TableCell align="center" rowSpan={sameCertificationRows.length}
                                          sx={{
                                            // 첨부: hasCertificationDoc가 true인 경우
                                            ...((selectedLegends.includes('첨부') || legendHover === '첨부') && row.productName.hasCertificationDoc && { color: '#2EA2DE', backgroundColor: 'rgba(25, 118, 210, 0.12)' }),
                                            // 만료: hasCertificationDoc가 false인 경우
                                            ...((selectedLegends.includes('만료') || legendHover === '만료') && row.productName.hasCertificationDoc && (row.productName.valid === false && row.productName.validYN === false) && { color: '#DB4E2E', backgroundColor: 'rgba(255, 0, 0, 0.08)' }),
                                            // 없음(필수): hasCertificationDoc가 false인 경우
                                            ...((selectedLegends.includes('없음(필수)') || legendHover === '없음(필수)') && !row.productName.hasCertificationDoc && { backgroundColor: 'rgba(100, 100, 100, 0.25)' }),
                                          }}
                                        >
                                          {row.productName?.hasCertificationDoc ? '●' : '×'}
                                        </TableCell>
                                      </>
                                    );
                                  }
                                  return null;
                                })()}
                                <TableCell align="center"
                                  sx={{
                                    // 첨부: hasDoc가 true이고 isValid/isValidYN이 true인 경우
                                    ...((selectedLegends.includes('첨부') || legendHover === '첨부') && row.productName.hasDoc && (row.productName.isValid === true || row.productName.isValidYN === true) && { color: '#2EA2DE', backgroundColor: 'rgba(25, 118, 210, 0.12)' }),
                                    // 만료: hasDoc가 true이지만 isValid/isValidYN이 false인 경우
                                    ...(
                                      (selectedLegends.includes('만료') || legendHover === '만료') && 
                                      (row.productName.hasDoc && row.productName.isValid === false && (row.productName.isValidYN === false || row.productName.isValidYN === undefined)) && 
                                        { color: '#DB4E2E', backgroundColor: 'rgba(255, 0, 0, 0.08)' }
                                    ),
                                    // 성능확인서: hasDoc가 false이고 hasPerformanceDoc가 true인 경우
                                    ...((selectedLegends.includes('성능확인서') || legendHover === '성능확인서') && !row.productName.hasDoc && row.productName.hasPerformanceDoc && { color: '#1976d2', backgroundColor: 'rgba(25, 118, 210, 0.25)' }),
                                    // 없음(필수): hasDoc가 false이고 hasPerformanceDoc도 false인 경우 (필수 문서)
                                    ...((selectedLegends.includes('없음(필수)') || legendHover === '없음(필수)') && !row.productName.hasDoc && !row.productName.hasPerformanceDoc && { backgroundColor: 'rgba(100, 100, 100, 0.25)' }),
                                  }}
                                >
                                  {(() => {
                                    console.log('Product data:', { productName: row.productName, legendHover });
                                    return null;
                                  })()}
                                  {row.productName.hasDoc ? 
                                    (row.productName.isValid || row.productName.isValidYN ? '●' : '▲') :
                                    (row.productName.hasPerformanceDoc ? '○' : '×')}
                                </TableCell>
                                <TableCell align="center"
                                  sx={{
                                    // 첨부: 성적서가 첨부+유효하고 비교표도 있을 때
                                    ...((selectedLegends.includes('첨부') || legendHover === '첨부')
                                      && row.productName.hasComparisonDoc
                                      && row.productName.hasDoc
                                      && (row.productName.isValid === true || row.productName.isValidYN === true)
                                      && { color: '#2EA2DE', backgroundColor: 'rgba(25, 118, 210, 0.12)' }),
                                    // 만료: 성적서가 첨부+만료이고 비교표도 있을 때
                                    ...((selectedLegends.includes('만료') || legendHover === '만료')
                                      && row.productName.hasComparisonDoc
                                      && row.productName.hasDoc
                                      && (row.productName.isValid === false && (row.productName.isValidYN === false || row.productName.isValidYN === undefined))
                                      && { color: '#DB4E2E', backgroundColor: 'rgba(255, 0, 0, 0.08)' }),
                                    // 없음(선택): 비교표가 없을 때
                                    ...((selectedLegends.includes('없음(선택)') || legendHover === '없음(선택)')
                                      && !row.productName.hasComparisonDoc
                                      && { backgroundColor: 'rgba(100, 100, 100, 0.10)' }),
                                  }}
                                >
                                  {row.productName.hasComparisonDoc
                                    ? (row.productName.hasDoc
                                        ? ((row.productName.isValid || row.productName.isValidYN) ? '●' : '▲')
                                        : '▲')
                                    : '-'}
                                </TableCell>
                                {(() => {
                                  const currentIndex = documentTableData.rows.findIndex(r => r.id === row.id);
                                  const sameCompanyRows = [];
                                  let i = currentIndex;
                                  
                                  while (i < documentTableData.rows.length && 
                                        documentTableData.rows[i].company === row.company) {
                                    sameCompanyRows.push(documentTableData.rows[i]);
                                    i++;
                                  }
                                  
                                  if (currentIndex === 0 || 
                                      documentTableData.rows[currentIndex - 1].company !== row.company) {
                                    return (
                                      <TableCell align="center" rowSpan={sameCompanyRows.length}
                                        sx={{
                                          // 없음(선택): etcDocs가 없거나 모든 문서가 hasPath가 false인 경우
                                          ...((selectedLegends.includes('없음(선택)') || legendHover === '없음(선택)') && (!row.etcDocs || row.etcDocs.length === 0 || row.etcDocs.every(doc => !doc.hasPath)) && { backgroundColor: 'rgba(100, 100, 100, 0.10)' }),
                                        }}
                                      >
                                        {row.etcDocs && row.etcDocs.length > 0 ? (
                                          <Box sx={{ whiteSpace: 'pre-line', textAlign: 'left' }}>
                                            {/* {row.etcDocs.map((doc, index) => (
                                              doc.hasPath ? ( 
                                                <Typography 
                                                  key={index} 
                                                  variant="body2"
                                                  sx={{
                                                    color: doc.hasPath ? 'inherit' : 'text.secondary',
                                                    // 기타 문서의 경우 첨부 상태만 하이라이트
                                                    ...((selectedLegends.includes('첨부') || legendHover === '첨부') && doc.hasPath && { color: '#2EA2DE', backgroundColor: 'rgba(25, 118, 210, 0.12)' })
                                                  }}
                                                >
                                                  • {doc.label}
                                                </Typography> 
                                              ) : null
                                            ))} */}
                                            {
                                              row.etcDocs.filter(doc => doc.hasPath).length > 0 ?
                                                row.etcDocs.map((doc, index) => (
                                                  <Typography 
                                                    key={index} 
                                                    variant="body2"
                                                    sx={{
                                                      color: doc.hasPath ? 'inherit' : 'text.secondary',
                                                      // 기타 문서의 경우 첨부 상태만 하이라이트
                                                      ...((selectedLegends.includes('첨부') || legendHover === '첨부') && doc.hasPath && { color: '#2EA2DE', backgroundColor: 'rgba(25, 118, 210, 0.12)' })
                                                    }}
                                                  >
                                                    • {doc.label}
                                                  </Typography> 
                                                ))
                                                : <Box sx={{ textAlign: 'center' }}>-</Box>
                                            }
                                          </Box>
                                        ) : '-'}
                                      </TableCell>
                                    );
                                  }
                                  return null;
                                })()}
                              </>
                            )}
                          </TableRow>
                        );
                      });
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>
    </Box>
  );
};

export default GProjectG04ContentTableView;